import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Smartphone, CheckCircle, XCircle, Key } from 'lucide-react';
import { toast } from 'sonner';

const MFASetup = ({ reducedMotion }) => {
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [settingUp, setSettingUp] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleEnableMFA = async () => {
    setSettingUp(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/mfa/enable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      setQrCode(data.qr_code_url);
      toast.info('Scan the QR code with your authenticator app');
    } catch (error) {
      toast.error('Failed to enable MFA');
    } finally {
      setSettingUp(false);
    }
  };

  const handleVerifyMFA = () => {
    if (verificationCode.length === 6) {
      setMfaEnabled(true);
      setQrCode(null);
      setVerificationCode('');
      toast.success('MFA enabled successfully');
    } else {
      toast.error('Please enter a 6-digit code');
    }
  };

  const handleDisableMFA = () => {
    setMfaEnabled(false);
    toast.info('MFA disabled');
  };

  return (
    <Card className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-slate-700/50 backdrop-blur-xl" data-testid="mfa-setup-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl text-slate-100 flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-green-400" />
              Multi-Factor Authentication
            </CardTitle>
            <CardDescription className="text-slate-400 mt-1">
              Add an extra layer of security to your admin panel
            </CardDescription>
          </div>
          <Badge
            className={mfaEnabled ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-slate-500/20 text-slate-400 border-slate-500/30'}
            data-testid="mfa-status"
          >
            {mfaEnabled ? (
              <><CheckCircle className="w-3 h-3 mr-1" /> Enabled</>
            ) : (
              <><XCircle className="w-3 h-3 mr-1" /> Disabled</>
            )}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {!mfaEnabled && !qrCode && (
            <>
              <div className="p-6 bg-slate-800/30 rounded-lg border border-slate-700/50 text-center">
                <Smartphone className="w-16 h-16 mx-auto mb-4 text-slate-600" />
                <h4 className="text-lg font-medium text-slate-100 mb-2">Secure Your Account</h4>
                <p className="text-sm text-slate-400 mb-4">
                  Enable two-factor authentication to protect your admin panel with an extra layer of security.
                </p>
                <Button
                  onClick={handleEnableMFA}
                  disabled={settingUp}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="enable-mfa-button"
                >
                  {settingUp ? 'Setting up...' : 'Enable MFA'}
                </Button>
              </div>

              <div className="p-4 bg-ink-500 rounded-lg border border-ink-300 dark:border-ink-700">
                <p className="text-xs text-slate-400">
                  <strong className="text-ink-500">What you'll need:</strong><br />
                  An authenticator app like Google Authenticator, Authy, or 1Password on your mobile device.
                </p>
              </div>
            </>
          )}

          {qrCode && (
            <>
              <div className="p-6 bg-slate-800/30 rounded-lg border border-slate-700/50 text-center">
                <h4 className="text-lg font-medium text-slate-100 mb-4">Scan QR Code</h4>
                <div className="w-48 h-48 mx-auto mb-4 bg-white rounded-lg flex items-center justify-center">
                  <img src={qrCode} alt="MFA QR Code" className="w-40 h-40" data-testid="mfa-qr-code" />
                </div>
                <p className="text-sm text-slate-400 mb-4">
                  Scan this QR code with your authenticator app, then enter the 6-digit code below.
                </p>
              </div>

              <div>
                <label className="text-xs text-slate-400 mb-2 block">Verification Code</label>
                <Input
                  type="text"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="bg-slate-800 border-slate-700 text-center text-2xl font-mono tracking-widest"
                  data-testid="verification-code-input"
                />
              </div>

              <Button
                onClick={handleVerifyMFA}
                disabled={verificationCode.length !== 6}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                data-testid="verify-mfa-button"
              >
                <Key className="w-4 h-4 mr-2" />
                Verify and Enable
              </Button>
            </>
          )}

          {mfaEnabled && (
            <>
              <div className="p-6 bg-emerald-500/10 rounded-lg border border-emerald-500/20 text-center">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-400" />
                <h4 className="text-lg font-medium text-emerald-400 mb-2">MFA is Active</h4>
                <p className="text-sm text-slate-400 mb-4">
                  Your account is now protected with two-factor authentication. You'll need your authenticator app to sign in.
                </p>
              </div>

              <div className="space-y-2">
                <h4 className="text-sm font-medium text-slate-300">Recovery Options</h4>
                <div className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/50">
                  <p className="text-xs text-slate-400">
                    Backup codes: <span className="text-slate-300 font-mono">XXXX-XXXX-XXXX</span>
                  </p>
                </div>
              </div>

              <Button
                onClick={handleDisableMFA}
                variant="outline"
                className="w-full border-red-700 text-red-400 hover:bg-red-950"
                data-testid="disable-mfa-button"
              >
                Disable MFA
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MFASetup;